/**
 * Header Component
 * Top header with system status and user info
 */

import React from 'react';
import { Layout, Space, Typography, Dropdown, Avatar, Badge } from 'antd';
import type { MenuProps } from 'antd';
import {
  BellOutlined,
  UserOutlined,
  LogoutOutlined,
  SettingOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import StatusBadge from '../StatusBadge';
import type { HealthStatus } from '../../types';

const { Header: AntHeader } = Layout;
const { Text } = Typography;

interface HeaderProps {
  collapsed: boolean;
  systemStatus: HealthStatus;
  onLogout?: () => void;
}

const Header: React.FC<HeaderProps> = ({ collapsed, systemStatus, onLogout }) => {
  const userMenuItems: MenuProps['items'] = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Profile',
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: 'Settings',
    },
    {
      type: 'divider',
    },
    {
      key: 'help',
      icon: <QuestionCircleOutlined />,
      label: 'Help & Documentation',
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      danger: true,
    },
  ];

  const handleUserMenuClick: MenuProps['onClick'] = ({ key }) => {
    if (key === 'logout' && onLogout) {
      onLogout();
    }
  };

  return (
    <AntHeader
      style={{
        position: 'fixed',
        top: 0,
        left: collapsed ? 80 : 220,
        right: 0,
        zIndex: 100,
        background: '#fff',
        padding: '0 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
        transition: 'left 0.2s',
      }}
    >
      {/* Left Side - Page Title / Breadcrumb */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <Text
          strong
          style={{
            fontSize: 16,
            color: '#001529',
          }}
        >
          Health Check System
        </Text>
        <StatusBadge status={systemStatus} showLabel />
      </div>

      {/* Right Side - Actions */}
      <Space size={24}>
        {/* Notifications */}
        <Badge count={3} size="small">
          <BellOutlined
            style={{
              fontSize: 18,
              color: '#595959',
              cursor: 'pointer',
            }}
          />
        </Badge>

        {/* User Menu */}
        <Dropdown
          menu={{
            items: userMenuItems,
            onClick: handleUserMenuClick,
          }}
          trigger={['click']}
          placement="bottomRight"
        >
          <Space style={{ cursor: 'pointer' }}>
            <Avatar
              size="small"
              icon={<UserOutlined />}
              style={{ backgroundColor: '#1890ff' }}
            />
            <Text style={{ color: '#595959' }}>Admin</Text>
          </Space>
        </Dropdown>
      </Space>
    </AntHeader>
  );
};

export default Header;
